/**
 * Auth types and validation for the identity domain.
 * Used by auth.service, auth-provider hooks, and AuthContext (re-exported for UI).
 */

export type UserRole = "platform_admin" | "org_admin" | "user" | "viewer";

export interface AuthUser {
  orgIds: string[];
  userId: string;
  email: string;
  role: UserRole;
}

/**
 * Sanitize user data from backend response.
 * Only extract fields we explicitly expect - prevents storing sensitive data.
 */
export function sanitizeUserData(data: any): AuthUser | null {
  const userId = data?.userId || data?.id;
  if (!data || !userId) {
    return null;
  }

  let orgIds: string[] = [];
  if (Array.isArray(data.orgIds) && data.orgIds.length > 0) {
    orgIds = data.orgIds.map((id: any) => String(id));
  } else if (data.orgId) {
    orgIds = [String(data.orgId)];
  } else {
    return null;
  }

  let roleValue: string | undefined = data.role;
  if (!roleValue && Array.isArray(data.roles) && data.roles.length > 0) {
    const firstRole = data.roles[0];
    roleValue =
      typeof firstRole === "string"
        ? firstRole
        : firstRole?.name || firstRole?.value || firstRole?.role;
  }

  const validRoles = ["platform_admin", "org_admin", "user", "viewer"];
  const normalizedRole =
    roleValue && validRoles.includes(roleValue) ? roleValue : "user";

  return {
    orgIds,
    userId: String(userId),
    email: String(data.email || ""),
    role: normalizedRole as UserRole,
  };
}
